import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Days, Icon } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { TempIService, ServiceCategoryAdminResponse, ServiceStatusAdminResponse } from 'src/app/global/services/service-state/service-state.model';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
import { serviceCategoriesSelector, serviceStatusesSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { Store, select } from '@ngrx/store';

@Component({
    selector: 'app-service-detail-dialog',
    templateUrl: './service-detail-dialog.component.html',
    styleUrls: ['./service-detail-dialog.component.scss'],
})
export class ServiceDetailDialogComponent implements OnInit {
    @Output() starredChange = new EventEmitter<boolean>();

    Icon = Icon;

    fallbackImg: string;

    formattedDayList = [];

    serviceCategory: ServiceCategoryAdminResponse;

    get getStarredClasses() {
        if (this.data.service.starred) {
            return `${Icon.STAR_FILLED} starred`;
        }
        return `${Icon.STAR}`;
    }

    get serviceName(): string {
        if (this.data.service && this.data.service.name && this.data.service.name[this.translate.currentLang]) {
            return this.data.service.name[this.translate.currentLang];
        }
        return '';
    }

    get serviceOverrideName(): string {
        if (this.data.service && this.data.service?.override) {
            return this.data.service.override.status?.name[this.translate.currentLang];
        }
        return '';
    }

    get addressString() {
        if (!this.data.service.address) return '';
        return `
      ${this.data.service.address.number}, 
      ${this.data.service.address.streetName}${' ' + this.data.service.address.direction}, 
      ${this.data.service.address.city}, 
      ${this.data.service.address.province}, 
      ${this.data.service.address.postCode}
    `;
    }

    get serviceDescription(): string {
        if (this.data.service && this.data.service.description && this.data.service.description[this.translate.currentLang]) {
            return this.data.service.description[this.translate.currentLang];
        }
        return '';
    }

    get firstDayOfWeek() {
        const now = new Date();
        const date = now.setDate(now.getDate() - now.getDay() + 1);
        const options = { month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, options as any).format(date);
    }

    get lastDayOfWeek() {
        const now = new Date();
        const date = now.setDate(now.getDate() - now.getDay() + 7);
        const options = { month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, options as any).format(date);
    }

    get imageUrl() {
        if (!this.data.service.image) {
            return this.fallbackImg;
        }

        return this.data.service.image;
    }

    getDate(day) {
        const dayNumber = this.getDayIndex(day);
        const now = new Date();
        const currentDay = now.getDay();
        const difference = dayNumber - currentDay;
        const date = now.setDate(now.getDate() + difference);
        return date;
    }

    getFormattedDay(date: Date) {
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, { weekday: 'short' }).format(date).toUpperCase();
    }

    getFormattedDate(date: Date) {
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, { month: 'short', day: 'numeric' }).format(date);
    }

    getDayIndex(day: Days) {
        switch (day) {
            case Days.MONDAY:
                return 1;
            case Days.TUESDAY:
                return 2;
            case Days.WEDNESDAY:
                return 3;
            case Days.THURSDAY:
                return 4;
            case Days.FRIDAY:
                return 5;
            case Days.SATURDAY:
                return 6;
            case Days.SUNDAY:
                return 7;
        }
    }

    get dayList() {
        const days = Object.keys(this.data.service.schedules[0].schedule);
        return days;
    }

    public getHourList(day: Days) {
        const hourList = this.data.service.schedules[0].schedule[day];
        if (!hourList) {
            return [];
        }
        return hourList;
    }

    constructor(
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        @Inject(MAT_DIALOG_DATA) public data: { service: TempIService; translatedValues: any; lang: string },
        private dialogRef: MatDialogRef<ServiceDetailDialogComponent>,
        public translate: TranslateService,
        public store: Store<State>,
    ) {
        this.fallbackImg = this.contextService.getBcitiPlusLogo();
    }

    ngOnInit(): void {
        this.translate.use(this.data.lang);
        this.formatDayList();
        if (this.data.service.category) {
            this.store.pipe(select(serviceCategoriesSelector)).subscribe((categories: ServiceCategoryAdminResponse[]) => {
                this.serviceCategory = categories.find((category) => category._id === (this.data.service.category as any));
            });
        }
    }

    onStarredClick(e): void {
        this.starredChange.emit(e);
    }

    close(): void {
        this.dialogRef.close();
    }

    formatDayList(): void {
        const days = Object.keys(this.data.service.schedules[0].schedule);
        const dayList = [];
        for (const day of days) {
            const hourList = this.data.service.schedules[0].schedule[day];
            if (!hourList) {
                continue;
            }
            dayList.push({
                day,
                date: this.getDate(day),
                hourList,
            });
        }
        this.formattedDayList = dayList.sort((a, b) => {
            return this.getDayIndex(a.day) - this.getDayIndex(b.day);
        });
    }
}
